/**
 * free-gift-popup.js
 * WindowCleaner Free Gift Popup
 *
 * What this file does:
 *   1. Reads the current cart via Shopify AJAX Cart API (/cart.js)
 *   2. Calls our PHP proxy (/api/tiers.php) to get tier data from metaobjects
 *   3. Shows ALL products from ALL tiers, grouped by tier section
 *      - Tiers the customer qualifies for: buttons are active
 *      - Tiers the customer does NOT qualify for: buttons are disabled/greyed out
 *        and a "Add $xx.xx more to qualify" message is shown under the tier header
 *   4. Shows a modal popup with those products (WITH VARIANT SUPPORT)
 *   5. When the customer clicks a product, adds it to cart with _is_free_gift=true
 *      and _gift_threshold=<cents> properties
 *   6. If a free gift is already in the cart and the customer picks a different one,
 *      removes the old one first then adds the new one
 *   7. If the cart drops below all thresholds, removes any existing free gift automatically
 *
 * Expected tier object shape (from /api/tiers.php):
 *   {
 *     threshold: 10000,          // in cents
 *     title: "Spend $100+",      // tier display title
 *     products: [
 *       {
 *         product_id, title, handle, image_url, image_alt,
 *         variant_id, variant_title, price, available
 *       }, ...
 *     ]
 *   }
 */

(function () {
  'use strict';

  // -------------------------------------------------------------------------
  // Config (set by the Liquid block)
  // -------------------------------------------------------------------------
  var cfg        = window.freeGiftConfig || {};
  var SHOP       = cfg.shopDomain || '';
  var APP_URL    = cfg.appUrl     || '';
  var CURRENCY   = cfg.currency   || 'USD';

  // Simple in-memory cache so we don't re-fetch tiers on every cart update
  var tiersCache = null;

  // -------------------------------------------------------------------------
  // Shopify AJAX Cart API helpers
  // -------------------------------------------------------------------------

  /** GET /cart.js — returns full cart object */
  function getCart() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); });
  }

  /**
   * POST /cart/add.js — adds a variant to cart with properties
   * @param {string} variantGid      - e.g. "gid://shopify/ProductVariant/12345"
   * @param {number} thresholdCents  - tier threshold in cents
   */
  function addGiftToCart(variantGid, thresholdCents) {
    var numericId = extractNumericId(variantGid);

    return fetch('/cart/add.js', {
      method:      'POST',
      credentials: 'same-origin',
      headers:     { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id:         numericId,
        quantity:   1,
        properties: {
          '_is_free_gift':   'true',
          '_gift_threshold': String(thresholdCents)
        }
      })
    }).then(function (r) { 
        return r; 
    });
  }

  /**
   * POST /cart/change.js — sets quantity of a line to 0 (removes it)
   * @param {string} lineKey - the cart line key (item.key from /cart.js)
   */
  function removeFromCart(lineKey) {
    return fetch('/cart/change.js', {
      method:      'POST',
      credentials: 'same-origin',
      headers:     { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: 0 })
    }).then(function (r) { return r.json(); });
  }

  // -------------------------------------------------------------------------
  // Fetch tier data from our PHP proxy
  // -------------------------------------------------------------------------

  function fetchTiers() {
    if (tiersCache !== null) {
      return Promise.resolve(tiersCache);
    }

    var url = APP_URL + '/api/tiers.php?shop=' + encodeURIComponent(SHOP);

    return fetch(url)
      .then(function (r) {
        if (!r.ok) {
          throw new Error('Tier API returned HTTP ' + r.status);
        }
        return r.json();
      })
      .then(function (data) {
        if (data.error) {
          throw new Error('Tier API error: ' + data.error);
        }
        // Sort by threshold ascending
        var tiers = (data.tiers || []).slice().sort(function (a, b) {
          return a.threshold - b.threshold;
        });
        tiersCache = tiers;
        return tiers;
      });
  }

  // -------------------------------------------------------------------------
  // Eligibility helpers
  // -------------------------------------------------------------------------

  /**
   * Returns true if the cart qualifies for at least one tier.
   */
  function hasAnyEligibleTier(subtotalCents, tiers) {
    for (var i = 0; i < tiers.length; i++) {
      if (subtotalCents >= tiers[i].threshold) {
        return true;
      }
    }
    return false;
  }

  /**
   * Find any existing free gift line item in the cart
   * (added via the popup — has _is_free_gift property).
   * @param {Array} items - cart.items from /cart.js
   * @returns {Object|null}
   */
  function findExistingGift(items) {
    for (var i = 0; i < items.length; i++) {
      var item  = items[i];
      var props = item.properties || {};
      if (props['_is_free_gift'] === 'true') {
        return item;
      }
    }
    return null;
  }

  /**
   * Checks whether any cart item matches a gift variant from any tier
   * (regardless of _is_free_gift property — catches gifts added outside the popup).
   * @param {Array} items - cart.items from /cart.js
   * @param {Array} tiers - all tiers
   * @returns {boolean}
   */
  function hasAnyGiftInCart(items, tiers) {
    // Build a lookup of all numeric variant IDs across all tiers
    var giftVariantIds = {};
    for (var i = 0; i < tiers.length; i++) {
      var products = tiers[i].products || [];
      for (var j = 0; j < products.length; j++) {
        var numId = String(extractNumericId(products[j].variant_id));
        giftVariantIds[numId] = true;
      }
    }

    for (var k = 0; k < items.length; k++) {
      if (giftVariantIds[String(items[k].variant_id)]) {
        return true;
      }
    }
    return false;
  }

  // -------------------------------------------------------------------------
  // DOM helpers
  // -------------------------------------------------------------------------

  function el(id) {
    return document.getElementById(id);
  }

  function showOverlay() {
    var overlay = el('fg-overlay');
    if (overlay) {
      overlay.classList.add('fg-visible');
      overlay.style.display = 'flex';
    }
  }

  function hideOverlay() {
    var overlay = el('fg-overlay');
    if (overlay) {
      overlay.classList.remove('fg-visible');
      overlay.style.display = 'none';
    }
  }

  function setStatus(msg) {
    var statusEl = el('fg-status');
    if (statusEl) {
      statusEl.textContent = msg;
      statusEl.style.display = msg ? 'block' : 'none';
    }
  }

  /**
   * Format cents as a currency string, e.g. 12550 → "$125.50"
   */
  function formatCents(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  /**
   * Format a product price string (assumed to be in dollars already), e.g. "49.99" → "$49.99"
   */
  function formatPrice(priceStr) {
    var num = parseFloat(priceStr) || 0;
    return '$' + num.toFixed(2);
  }

  /**
   * Get the display title for a product variant
   * Shows: "Product Title - Variant Title" or just "Product Title" if no variant
   */
  function getDisplayTitle(product) {
    var productTitle = product.title || 'Product';
    var variantTitle = product.variant_title;
    
    if (variantTitle && variantTitle !== 'Default Title') {
      return productTitle + ' - ' + variantTitle;
    }
    return productTitle;
  }

  /**
   * Get the best available image for a variant
   * Priority: variant image > product image > placeholder
   */
  function getVariantImage(product) {
    // If variant has its own image URL, use it
    if (product.variant_image_url && product.variant_image_url !== '') {
      return product.variant_image_url;
    }
    // Fall back to product image
    if (product.image_url && product.image_url !== '') {
      return product.image_url;
    }
    return null;
  }

  // -------------------------------------------------------------------------
  // Build and render the products grid — split by tier (WITH VARIANT SUPPORT)
  // -------------------------------------------------------------------------

  /**
   * Renders all tiers with their products into #fg-products-list.
   * Each tier gets a section header + optional "Add $xx.xx more" message.
   * Products in tiers the customer hasn't reached yet are visually disabled.
   * Now supports variant-specific titles and images.
   *
   * @param {Array}       tiers          - all tiers, sorted ascending by threshold
   * @param {number}      subtotalCents  - current cart subtotal in cents
   * @param {Object|null} existingGift   - current free-gift line item, or null
   */
  function renderProducts(tiers, subtotalCents, existingGift) {
    var listEl = el('fg-products-list');
    if (!listEl) return;

    listEl.innerHTML = '';

    if (!tiers || tiers.length === 0) {
      setStatus('No gift products available right now.');
      return;
    }

    setStatus('');

    for (var i = 0; i < tiers.length; i++) {
      var tier           = tiers[i];
      var qualified      = subtotalCents >= tier.threshold;
      var shortfall      = tier.threshold - subtotalCents; // > 0 means not yet qualified
      var products       = tier.products || [];

      if (products.length === 0) continue;

      // ── Tier section wrapper ────────────────────────────────────────────
      var section = document.createElement('div');
      section.className = 'fg-tier-section' + (qualified ? ' fg-tier-qualified' : ' fg-tier-locked');

      // Tier header
      var header = document.createElement('div');
      header.className = 'fg-tier-header';

      var tierTitle = document.createElement('h3');
      tierTitle.className = 'fg-tier-title';
      tierTitle.textContent = tier.title
        ? tier.title
        : ('Spend ' + formatCents(tier.threshold) + ' USD or More');
      header.appendChild(tierTitle);

      // "Add $xx.xx more to qualify" message (only for locked tiers)
      if (!qualified) {
        var qualifyMsg = document.createElement('p');
        qualifyMsg.className = 'fg-tier-qualify-msg';
        qualifyMsg.textContent = 'Add ' + formatCents(shortfall) + ' to qualify for these items.';
        header.appendChild(qualifyMsg);
      }

      if (isInternationalVisitor()) {
        var intlMsg = document.createElement('p');
        intlMsg.className = 'fg-tier-intl-msg';
        intlMsg.textContent =
          'Free gift tiers are based on USD totals. ' +
          'Orders in other currencies qualify once they meet the ' +
          'equivalent amount at today\u2019s exchange rate.';
        header.appendChild(intlMsg);
      }

      section.appendChild(header);

      // ── Products grid for this tier ────────────────────────────────────
      var grid = document.createElement('div');
      grid.className = 'fg-tier-grid';

      for (var j = 0; j < products.length; j++) {
        var product = products[j];

        // Get variant-specific display information
        var displayTitle = getDisplayTitle(product);
        var variantImage = getVariantImage(product);
        var altText = product.variant_image_alt || product.image_alt || displayTitle;

        // Is this the currently selected free gift?
        var isSelected = false;
        if (existingGift) {
          var existingNumericId = String(existingGift.variant_id);
          var thisNumericId     = String(extractNumericId(product.variant_id));
          isSelected = (existingNumericId === thisNumericId);
        }

        var card = document.createElement('div');
        card.className = 'fg-product-card'
          + (isSelected  ? ' fg-selected' : '')
          + (!qualified  ? ' fg-card-locked' : '');
        card.setAttribute('data-variant-id',     product.variant_id);
        card.setAttribute('data-tier-threshold', String(tier.threshold));

        console.log(product)

        // Image or placeholder (use variant image if available)
        if (variantImage) {
          var img     = document.createElement('img');
          img.src     = variantImage;
          img.alt     = altText;
          img.loading = 'lazy';
          card.appendChild(img);
        } else {
          var placeholder     = document.createElement('div');
          placeholder.className = 'fg-no-image';
          placeholder.textContent = '🎁';
          card.appendChild(placeholder);
        }

        // Product title (with variant if applicable)
        var titleEl       = document.createElement('div');
        titleEl.className = 'fg-product-title';
        titleEl.textContent = displayTitle;
        card.appendChild(titleEl);

        // Product title (as link with variant support)
        /*var titleLink = document.createElement('a');
        titleLink.className = 'fg-product-title';
        titleLink.textContent = displayTitle;
        var productHandle = product.handle || '';
        var variantId = extractNumericId(product.variant_id);
        if (productHandle) {
          titleLink.href = '/products/' + productHandle + '?variant=' + variantId;
          titleLink.target = '_blank';
          titleLink.rel = 'noopener';
        } else {
          titleLink.href = '#';
        }
        card.appendChild(titleLink);*/

        // Optional: Show variant badge if this is a variant
        if (product.variant_title && product.variant_title !== 'Default Title') {
          var variantBadge = document.createElement('div');
          variantBadge.className = 'fg-variant-badge';
          variantBadge.textContent = product.variant_title;
          card.appendChild(variantBadge);
        }

        // Original price (crossed out)
        var priceEl       = document.createElement('div');
        priceEl.className = 'fg-original-price';
        priceEl.textContent = formatPrice(product.price);
        card.appendChild(priceEl);

        // FREE badge
        var badge       = document.createElement('div');
        badge.className = 'fg-free-badge';
        badge.textContent = 'FREE';
        card.appendChild(badge);

        // Select button
        var btn       = document.createElement('button');
        btn.className = 'fg-select-btn';
        btn.type      = 'button';

        if (!qualified) {
          // Tier not reached yet — grey out
          btn.textContent = 'Add ' + formatCents(shortfall) + ' more to unlock';
          btn.disabled    = true;
          btn.classList.add('fg-btn-locked');
        } else if (!product.available) {
          btn.textContent = 'Unavailable';
          btn.disabled    = true;
        } else if (isSelected) {
          btn.textContent = 'Selected ✓';
          btn.disabled    = true;
        } else {
          btn.textContent = 'Select';
        }

        card.appendChild(btn);
        grid.appendChild(card);
      }

      section.appendChild(grid);
      listEl.appendChild(section);
    }

    // Attach click handlers only to enabled Select buttons
    var buttons = listEl.querySelectorAll('.fg-select-btn:not([disabled])');
    for (var b = 0; b < buttons.length; b++) {
      attachSelectHandler(buttons[b], existingGift);
    }
  }

  /**
   * Attaches the click handler to a single "Select" button.
   */
  function attachSelectHandler(btn, existingGift) {
    btn.addEventListener('click', function () {
      var card           = btn.closest('.fg-product-card') || btn.parentElement;
      var variantGid     = card.getAttribute('data-variant-id');
      var thresholdCents = parseInt(card.getAttribute('data-tier-threshold'), 10) || 0;

      // Disable all active buttons while processing
      var allBtns = document.querySelectorAll('.fg-select-btn:not([disabled])');
      for (var i = 0; i < allBtns.length; i++) {
        allBtns[i].disabled = true;
      }
      btn.textContent = 'Adding...';
      btn.disabled    = true;

      // Remove existing gift first (if any), then add the new one
      var removePromise = existingGift
        ? removeFromCart(existingGift.key)
        : Promise.resolve();

      removePromise
        .then(function () {
          return addGiftToCart(variantGid, thresholdCents);
        })
        .then(function () {
          // Mark the chosen card as selected
          var allCards = document.querySelectorAll('.fg-product-card');
          for (var c = 0; c < allCards.length; c++) {
            allCards[c].classList.remove('fg-selected');
            var cardBtn = allCards[c].querySelector('.fg-select-btn');
            // Only reset buttons that are not locked/unavailable
            if (cardBtn && !cardBtn.classList.contains('fg-btn-locked')) {
              var isUnavailable = cardBtn.textContent === 'Unavailable';
              if (!isUnavailable) {
                cardBtn.textContent = 'Select';
                cardBtn.disabled    = false;
              }
            }
          }
          card.classList.add('fg-selected');
          btn.textContent = 'Selected ✓';
          btn.disabled    = true;

          // Close modal and reload cart so prices update
          setTimeout(function () {
            hideOverlay();
            reloadCartPage();
          }, 800);
        })
        .catch(function (err) {
          console.error('[FreeGift] Error adding gift:', err);
          btn.textContent = 'Error — Try Again';
          btn.disabled    = false;

          // Re-enable all active (non-locked) buttons
          var allBtns2 = document.querySelectorAll('.fg-select-btn:not(.fg-btn-locked)');
          for (var i = 0; i < allBtns2.length; i++) {
            if (allBtns2[i].textContent !== 'Unavailable') {
              allBtns2[i].disabled = false;
            }
          }
        });
    });
  }

  // -------------------------------------------------------------------------
  // Reload cart page
  // -------------------------------------------------------------------------
  function reloadCartPage() {
    window.location.reload();
  }

  // -------------------------------------------------------------------------
  // Main popup open
  // -------------------------------------------------------------------------

  function openModal(tiers, existingGift, subtotalCents) {
    var subtitleEl = el('fg-subtitle');
    if (subtitleEl) {
      if (existingGift) {
        subtitleEl.textContent =
          'You have a free gift in your cart. You can swap it for a different one below:';
      } else {
        subtitleEl.textContent =
          'You qualify for a free gift! Choose one from the options below:';
      }
    }

    setStatus('');
    renderProducts(tiers, subtotalCents, existingGift);
    showOverlay();
  }

  // -------------------------------------------------------------------------
  // Main init
  // -------------------------------------------------------------------------

  function init() {
    if (!SHOP || !APP_URL) {
      console.warn('[FreeGift] Missing shopDomain or appUrl config.');
      return;
    }

    // Wire up the close button
    var closeBtn = el('fg-close-btn');
    if (closeBtn) {
      closeBtn.addEventListener('click', hideOverlay);
    }

    // Close when clicking the dark overlay (outside the modal box)
    var overlay = el('fg-overlay');
    if (overlay) {
      overlay.addEventListener('click', function (e) {
        if (e.target === overlay) {
          hideOverlay();
        }
      });
    }

    // Close on Escape key
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' || e.keyCode === 27) {
        hideOverlay();
      }
    });

    // Wire up the "Choose Your Free Gift" button
    var openBtn = el('fg-open-btn');
    if (openBtn) {
      openBtn.addEventListener('click', function () {
        Promise.all([getCart(), fetchTiers()])
          .then(function (results) {
            var cart          = results[0];
            var tiers         = results[1];
            var subtotalCents = getCartSubtotalInBaseCurrency(cart);
            var existingGift  = findExistingGift(cart.items);

            // Show modal as long as there is at least one tier defined
            if (tiers.length > 0) {
              openModal(tiers, existingGift, subtotalCents);
            }
          })
          .catch(function (err) {
            console.error('[FreeGift] Error opening modal:', err);
          });
      });
    }

    // On page load: check cart and show popup / button automatically
    checkCartAndUpdate();
  }

  /**
   * Core check: fetch cart + tiers, decide whether to show the popup,
   * show the open button, or silently remove a stale gift.
   */
  function checkCartAndUpdate() {
    Promise.all([getCart(), fetchTiers()])
      .then(function (results) {
        var cart          = results[0];
        var tiers         = results[1];
        var subtotalCents = getCartSubtotalInBaseCurrency(cart);
        var existingGift  = findExistingGift(cart.items);
        var anyEligible   = hasAnyEligibleTier(subtotalCents, tiers);

        var openBtn = el('fg-open-btn');

        if (anyEligible) {
          // Customer qualifies for at least one tier — show the button
          if (openBtn) {
            openBtn.style.display = 'block';
          }

          // Auto-open modal only if no gift product is already in the cart
          // (covers both gifts added via popup and gifts added directly from product page)
          var giftAlreadyInCart = existingGift || hasAnyGiftInCart(cart.items, tiers);
          if (!giftAlreadyInCart) {
            openModal(tiers, null, subtotalCents);
          }

        } else {
          // Customer doesn't qualify for any tier yet — hide the button
          if (openBtn) {
            openBtn.style.display = 'none';
          }

          // Remove any stale free gift they might still have in cart
          if (existingGift) {
            removeFromCart(existingGift.key)
              .then(function () {
                reloadCartPage();
              })
              .catch(function (err) {
                console.error('[FreeGift] Error removing ineligible gift:', err);
              });
          }
        }
      })
      .catch(function (err) {
        console.error('[FreeGift] Error during cart check:', err);
      });
  }

  // -------------------------------------------------------------------------
  // Utility: extract numeric ID from a Shopify GID string
  // e.g. "gid://shopify/ProductVariant/12345678" → 12345678
  // -------------------------------------------------------------------------
  function extractNumericId(gid) {
    if (!gid) return 0;
    var parts = String(gid).split('/');
    return parseInt(parts[parts.length - 1], 10) || 0;
  }

  // Convert presentment-currency cart total back to shop base currency
  // -------------------------------------------------------------------------
  function getCartSubtotalInBaseCurrency(cart) {
    var total = cart.total_price;

    try {
      // Shopify themes expose the active exchange rate on window.Shopify.currency
      var shopifyCurrency = window.Shopify && window.Shopify.currency;
      if (shopifyCurrency && typeof shopifyCurrency.rate === 'string') {
        var rate = parseFloat(shopifyCurrency.rate);
        if (rate && rate !== 1) {
          // cart.total_price is in the presentment currency; divide by rate to get USD
          total = Math.round(total / rate);
        }
      }
    } catch (e) {
      console.warn('[FreeGift] Could not read Shopify currency rate:', e);
    }

    return total;
  }

  function isInternationalVisitor() {
    try {
      var shopifyCurrency = window.Shopify && window.Shopify.currency;
      if (shopifyCurrency && shopifyCurrency.active) {
        return shopifyCurrency.active !== 'USD';
      }
    } catch (e) {}
    return false;
  }

  // -------------------------------------------------------------------------
  // Bootstrap
  // -------------------------------------------------------------------------
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();